import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { connectionAPI } from '../services/api';
import { Users, Search, UserPlus, UserCheck, UserX, Clock, Mail, Phone, MessageCircle } from 'lucide-react';

const NetworkPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('directory'); // directory, connections, requests
  const [dealers, setDealers] = useState([]);
  const [connections, setConnections] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'directory') {
        const response = await connectionAPI.getDirectory({ search: searchQuery });
        setDealers(response.data.dealers || []);
      } else if (activeTab === 'connections') {
        const response = await connectionAPI.getMyConnections();
        setConnections(response.data.connections || []);
      } else if (activeTab === 'requests') {
        const response = await connectionAPI.getPendingRequests();
        setIncomingRequests(response.data.incoming_requests || []);
        setOutgoingRequests(response.data.outgoing_requests || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (activeTab === 'directory') {
      fetchData();
    }
  };

  const handleSendRequest = async (dealerId) => {
    try {
      await connectionAPI.sendRequest(dealerId);
      alert('Connection request sent!');
      fetchData();
    } catch (error) {
      console.error('Error sending request:', error);
      alert(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const handleAcceptRequest = async (connectionId) => {
    try {
      await connectionAPI.acceptRequest(connectionId);
      alert('Connection accepted!');
      fetchData();
    } catch (error) {
      console.error('Error accepting request:', error);
      alert('Failed to accept request');
    }
  };

  const handleDeclineRequest = async (connectionId) => {
    try {
      await connectionAPI.declineRequest(connectionId);
      fetchData();
    } catch (error) {
      console.error('Error declining request:', error);
      alert('Failed to decline request');
    }
  };

  const handleRemoveConnection = async (connectionId) => {
    if (window.confirm('Remove this connection?')) {
      try {
        await connectionAPI.removeConnection(connectionId);
        fetchData();
      } catch (error) {
        console.error('Error removing connection:', error);
        alert('Failed to remove connection');
      }
    }
  };

  const handleViewProfile = (dealerId) => {
    navigate(`/network/profile/${dealerId}`);
  };

  const handleMessageDealer = (dealer) => {
    navigate(`/messages?userId=${dealer.dealer_id}&userName=${encodeURIComponent(dealer.dealer_name)}&businessName=${encodeURIComponent(dealer.company_name || '')}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Network</h1>
          <p className="text-gray-600 mt-1">Connect with other watch dealers</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-md border-2 border-gray-100">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('directory')}
            className={`flex-1 px-6 py-4 font-semibold transition-all ${
              activeTab === 'directory'
                ? 'text-slate-900 border-b-2 border-slate-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Users className="w-5 h-5 inline mr-2" />
            Dealer Directory
          </button>
          <button
            onClick={() => setActiveTab('connections')}
            className={`flex-1 px-6 py-4 font-semibold transition-all ${
              activeTab === 'connections'
                ? 'text-slate-900 border-b-2 border-slate-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <UserCheck className="w-5 h-5 inline mr-2" />
            My Connections ({connections.length})
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`flex-1 px-6 py-4 font-semibold transition-all relative ${
              activeTab === 'requests'
                ? 'text-slate-900 border-b-2 border-slate-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Clock className="w-5 h-5 inline mr-2" />
            Requests
            {incomingRequests.length > 0 && (
              <span className="absolute top-2 right-4 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                {incomingRequests.length}
              </span>
            )}
          </button>
        </div>

        {/* Directory Tab */}
        {activeTab === 'directory' && (
          <div className="p-6 space-y-6">
            {/* Search Bar */}
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search dealers by name or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-slate-900"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-all"
              >
                Search
              </button>
            </div>

            {/* Dealers Grid */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-900 border-t-transparent mx-auto"></div>
              </div>
            ) : dealers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No dealers found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dealers.map((dealer) => (
                  <div
                    key={dealer.id}
                    className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 border-2 border-gray-100 hover:border-slate-300 transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => handleViewProfile(dealer.id)}
                      >
                        <h3 className="text-lg font-bold text-gray-900 hover:text-slate-700">
                          {dealer.dealer_name}
                        </h3>
                        {dealer.company_name && (
                          <p className="text-sm text-gray-600">{dealer.company_name}</p>
                        )}
                        {(dealer.city || dealer.state) && (
                          <p className="text-sm text-gray-500 mt-1">
                            {dealer.city && dealer.city}
                            {dealer.city && dealer.state && ', '}
                            {dealer.state && dealer.state}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Connection Status & Actions */}
                    <div className="mt-4">
                      {dealer.connection_status === 'Accepted' ? (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-green-600 font-semibold flex items-center">
                            <UserCheck className="w-4 h-4 mr-1" />
                            Connected
                          </span>
                          <button
                            onClick={() => handleRemoveConnection(dealer.connection_id)}
                            className="text-sm text-red-600 hover:text-red-700 font-semibold"
                          >
                            Remove
                          </button>
                        </div>
                      ) : dealer.connection_status === 'Pending' ? (
                        dealer.is_incoming_request ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleAcceptRequest(dealer.connection_id)}
                              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all text-sm font-semibold"
                            >
                              Accept
                            </button>
                            <button
                              onClick={() => handleDeclineRequest(dealer.connection_id)}
                              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all text-sm font-semibold"
                            >
                              Decline
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm text-yellow-600 font-semibold flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            Request Sent
                          </span>
                        )
                      ) : (
                        <button
                          onClick={() => handleSendRequest(dealer.id)}
                          className="w-full px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-all font-semibold flex items-center justify-center gap-2"
                        >
                          <UserPlus className="w-4 h-4" />
                          Connect
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Connections Tab */}
        {activeTab === 'connections' && (
          <div className="p-6 space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-900 border-t-transparent mx-auto"></div>
              </div>
            ) : connections.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <UserCheck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No connections yet</p>
                <button
                  onClick={() => setActiveTab('directory')}
                  className="mt-4 px-6 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all font-semibold"
                >
                  Browse Dealers
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {connections.map((connection) => (
                  <div
                    key={connection.connection_id}
                    className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-100 hover:border-green-300 transition-all"
                  >
                    <div
                      className="cursor-pointer mb-4"
                      onClick={() => handleViewProfile(connection.dealer_id)}
                    >
                      <h3 className="text-lg font-bold text-gray-900 hover:text-slate-700">
                        {connection.dealer_name}
                      </h3>
                      {connection.company_name && (
                        <p className="text-sm text-gray-600">{connection.company_name}</p>
                      )}
                      {(connection.city || connection.state) && (
                        <p className="text-sm text-gray-500 mt-1">
                          {connection.city && connection.city}
                          {connection.city && connection.state && ', '}
                          {connection.state && connection.state}
                        </p>
                      )}

                      {/* Contact Info */}
                      {connection.email && (
                        <div className="flex items-center gap-2 mt-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <a
                            href={`mailto:${connection.email}`}
                            className="text-sm text-blue-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {connection.email}
                          </a>
                        </div>
                      )}
                      {connection.phone && (
                        <div className="flex items-center gap-2 mt-1">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <a
                            href={`tel:${connection.phone}`}
                            className="text-sm text-blue-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {connection.phone}
                          </a>
                        </div>
                      )}

                      <p className="text-xs text-gray-400 mt-2">
                        Connected {new Date(connection.connected_since).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleMessageDealer(connection)}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-semibold flex items-center justify-center gap-2"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Message
                      </button>
                      <button
                        onClick={() => handleRemoveConnection(connection.connection_id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-sm font-semibold"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="p-6 space-y-8">
            {/* Incoming Requests */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Incoming Requests ({incomingRequests.length})
              </h2>
              {incomingRequests.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No incoming requests</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {incomingRequests.map((request) => (
                    <div
                      key={request.connection_id}
                      className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-100"
                    >
                      <div className="mb-4">
                        <h3 className="text-lg font-bold text-gray-900">{request.dealer_name}</h3>
                        {request.company_name && (
                          <p className="text-sm text-gray-600">{request.company_name}</p>
                        )}
                        {(request.city || request.state) && (
                          <p className="text-sm text-gray-500 mt-1">
                            {request.city && request.city}
                            {request.city && request.state && ', '}
                            {request.state && request.state}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(request.requested_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAcceptRequest(request.connection_id)}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => handleDeclineRequest(request.connection_id)}
                          className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all font-semibold"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Outgoing Requests */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Sent Requests ({outgoingRequests.length})
              </h2>
              {outgoingRequests.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No pending sent requests</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {outgoingRequests.map((request) => (
                    <div
                      key={request.connection_id}
                      className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-6 border-2 border-yellow-100"
                    >
                      <div className="mb-4">
                        <h3 className="text-lg font-bold text-gray-900">{request.dealer_name}</h3>
                        {request.company_name && (
                          <p className="text-sm text-gray-600">{request.company_name}</p>
                        )}
                        {(request.city || request.state) && (
                          <p className="text-sm text-gray-500 mt-1">
                            {request.city && request.city}
                            {request.city && request.state && ', '}
                            {request.state && request.state}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          Sent {new Date(request.requested_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="text-sm text-yellow-600 font-semibold flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        Pending
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkPage;
